import React, { useCallback } from 'react';
import { CardView, rem } from 'design-system';
import Text from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import { postUserAddress } from 'actions';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { selectPhoneNumberRecentlyVerified, selectPrimaryAddressForGB, } from 'reducers/selectors';
import { OnboardingFlow } from 'utils/types/navigation';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import { isWeb } from '../../../constants';
import { setPendingPreviousAddress, setPendingPrimaryAddress, } from '../reducers/creditScoreOnboarding';
import selectCreditScoreKycVerification from '../selectors/kycVerification';
import { hasValidAddress } from '../utils';
import useCreditScoreRegistration from './useCreditScoreRegistration';
const strings = {
    addressModalTitle: {
        PENDING: 'Your address is being verified',
        FAIL: 'Your address couldn’t be verified',
    },
    addressModalSubtitle: {
        PENDING: `We’re currently verifying your new address. You’ll be able to unlock your credit score once verification is complete.`,
        FAIL: `There was an issue verifying your address. You'll need to update it before you can unlock your credit score.`,
    },
    verificationPending: 'Verification pending',
    verificationFailed: 'Rejected',
    changeAddress: 'Change address',
    updateAddress: 'Update address',
    okay: 'Okay',
    verificationModalSubtitle: `We need to confirm your identity before we can unlock your credit score.`,
    verificationModal: {
        NEEDS_ACTION: {
            title: 'Verify your details',
            description: {
                UPLOAD_IDENTITY_DOCUMENT: `Tap “Verify” to provide both proofs of your identity and address.`,
                UPLOAD_ADDRESS_AND_IDENTITY_DOCUMENTS: `Tap “Verify” to provide both proofs of your identity and address.`,
                UPLOAD_PROOF_OF_ADDRESS: `Tap “Verify” to provide proof of your address.`,
            },
            button: 'Verify',
        },
        IN_REVIEW: {
            title: 'We’re verifying your details',
            description: `We’ll let you know once your account is approved. This process usually takes minutes.`,
            button: 'Okay',
        },
        REJECTED: {
            title: 'We couldn’t verify your details',
            button: 'Verify again',
        },
    },
    reasonPrefix: 'Reason: ',
};
const getVerificationModalContent = (verification) => {
    const { NEEDS_ACTION, IN_REVIEW, REJECTED } = strings.verificationModal;
    if (verification.rejection) {
        return {
            ...REJECTED,
            reason: verification.rejection.rejectionReasonString,
        };
    }
    if (verification.requiredUserAction) {
        return {
            ...NEEDS_ACTION,
            description: NEEDS_ACTION.description[verification.requiredUserAction],
        };
    }
    return { ...IN_REVIEW, dismissOnly: true };
};
const getDocumentTypeToUpload = (verification) => {
    if (verification.requiredUserAction === 'UPLOAD_PROOF_OF_ADDRESS')
        return 'POA';
    if (verification.requiredUserAction)
        return 'POI';
    return verification.rejection?.type === 'ADDRESS_CHECK' ? 'POA' : 'POI';
};
const AddressModalContent = ({ subtitle, address }) => {
    const styles = useStyles(styleSet);
    return (<>
      <Text TextThin-14 Secondary centered={!isWeb}>
        {subtitle}
      </Text>
      <CardView withPadding noMargin style={styles.addressModalContent}>
        <FormattedAddress {...address} uk/>
      </CardView>
    </>);
};
const VerificationModalContent = ({ description, reason }) => {
    const styles = useStyles(styleSet);
    return (<>
      <Text TextThin-14 Secondary centered>
        {strings.verificationModalSubtitle}
      </Text>
      {!!description && (<Text TextThin-14 Secondary centered style={styles.paragraph}>
          {description}
        </Text>)}
      {!!reason && (<CardView withPadding noMargin style={styles.addressModalContent}>
          <Text TextThin-14 Secondary>
            {strings.reasonPrefix}
            {reason}
          </Text>
        </CardView>)}
    </>);
};
const showVerificationModal = (verification, onVerify) => {
    const { title, button, description, reason, dismissOnly } = getVerificationModalContent(verification);
    Modal.showConfirmation({
        title,
        subtitle: (<VerificationModalContent description={description} reason={reason}/>),
        confirmButtonTitle: button,
        onConfirm: dismissOnly ? undefined : onVerify,
    });
};
const useCreditScoreNavigation = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const { register, showLoading, hideLoading } = useCreditScoreRegistration(true);
    const [, , , fetchSave] = useFetchOld(muteErrorAndResult);
    const navigateToAddress = useCallback(() => {
        const state = store.getState();
        const primaryAddress = selectPrimaryAddressForGB(state);
        // No address yet — go to address input
        if (!hasValidAddress(primaryAddress)) {
            navigation.navigate('AddressPostCodeScreen', {
                flow: OnboardingFlow.CreditScore,
            });
            return;
        }
        // Has address, no KYC — review screen (sets move-in date, then handles
        // forward navigation to previous address / registration)
        navigation.navigate('CreditScoreReviewAddress');
    }, []);
    const navigateToNextStep = useCallback(() => {
        const state = store.getState();
        const primaryAddress = selectPrimaryAddressForGB(state);
        const kycVerification = selectCreditScoreKycVerification(state);
        // KYC verfication modal
        if (kycVerification) {
            showVerificationModal(kycVerification, () => {
                navigation.navigate('WealthDocumentUploadIntroScreen', {
                    documentType: getDocumentTypeToUpload(kycVerification),
                    flow: OnboardingFlow.CreditScore,
                });
            });
            return;
        }
        // Address KYC success flow
        const kycStatus = primaryAddress?.kycStatus;
        if (kycStatus === 'SUCCESS') {
            navigation.navigate('CreditScoreReviewKYCAddress');
            return;
        }
        // Address KYC verification modal
        if ((kycStatus === 'PENDING' || kycStatus === 'FAIL') && primaryAddress) {
            const title = strings.addressModalTitle[kycStatus];
            const subtitle = strings.addressModalSubtitle[kycStatus];
            const changeBtnText = kycStatus === 'PENDING' ? strings.changeAddress : strings.updateAddress;
            Modal.showConfirmation({
                title,
                subtitle: (<AddressModalContent subtitle={subtitle} address={primaryAddress}/>),
                cancelButtonTitle: strings.okay,
                confirmButtonTitle: changeBtnText,
                onConfirm: () => {
                    navigation.navigate('ChangeAddressProgressScreen', {
                        flow: 'CHANGE',
                    });
                },
            });
            return;
        }
        // If we're not already on PersonalDetails, send the user there first.
        // Once they're on PersonalDetails and submit, this hook is called again
        // and the conditional falls through to the next step.
        const { routes } = navigation.getState();
        const hasCreditScorePersonalDetails = routes.some((route) => route.name === 'CreditScorePersonalDetails');
        if (!hasCreditScorePersonalDetails) {
            navigation.navigate('CreditScorePersonalDetails');
            return;
        }
        // No phone number — verify one before moving on
        if (!state.user.user.phoneNumber) {
            navigation.navigate('PhoneVerification', {
                flow: 'creditScore',
                onSuccess: () => {
                    navigation.pop(2);
                    navigateToAddress();
                },
            });
            return;
        }
        // Number not verified recently (or verification date unknown) — ask the
        // user to confirm it
        if (!selectPhoneNumberRecentlyVerified(state)) {
            navigation.navigate('CreditContactDetails');
            return;
        }
        navigateToAddress();
    }, [navigateToAddress]);
    // Persists the address to Redux (not the API) and navigates back to the
    // appropriate credit-score screen. Replaces ConfirmAddressScreen for the
    // credit-score flow — see AddressInputScreen.
    const continueAddress = useCallback(async (address, isAddingSecondaryAddress) => {
        if (isAddingSecondaryAddress) {
            dispatch(setPendingPreviousAddress(address));
        }
        else {
            dispatch(setPendingPrimaryAddress(address));
        }
        const { routes } = navigation.getState();
        const isRegistrationInStack = routes.some((r) => r.name === 'CreditScoreRegistration');
        const isReviewInStack = routes.some((r) => r.name === 'CreditScoreReviewAddress');
        const isReviewKYCAddressInStack = routes.some((r) => r.name === 'CreditScoreReviewKYCAddress');
        const primaryAddress = selectPrimaryAddressForGB(store.getState());
        if (isReviewKYCAddressInStack &&
            isAddingSecondaryAddress &&
            primaryAddress) {
            // KYC flow registers straight from here, so the previous address has
            // to be persisted first — the non-KYC flow does the same POST on
            // CreditScoreRegistrationScreen before registering.
            let previousAddressId = address.id;
            if (!previousAddressId) {
                // register() shows the same loader, so the user sees one
                // uninterrupted loading state from press → registration result.
                showLoading();
                const result = await fetchSave(postUserAddress({ ...address, isPrimary: false }));
                if (!result?.address) {
                    hideLoading();
                    return;
                }
                previousAddressId = result.address.id;
            }
            register(primaryAddress.id, previousAddressId);
            return;
        }
        if (isRegistrationInStack) {
            navigation.popTo('CreditScoreRegistration', undefined, {
                merge: true,
            });
            return;
        }
        if (isReviewInStack && isAddingSecondaryAddress) {
            // Added a new previous address — go back to registration. If the user
            // got here via ChoosePreviousAddressScreen ("Add new address"), pop
            // back to it so the natural back navigation walks ChoosePrev →
            // ReviewAddress.
            const isChoosePreviousInStack = routes.some((r) => r.name === 'ChoosePreviousAddressScreen');
            if (isChoosePreviousInStack) {
                navigation.popTo('ChoosePreviousAddressScreen', undefined, {
                    merge: true,
                });
            }
            else {
                navigation.popTo('CreditScoreReviewAddress', undefined, {
                    merge: true,
                });
            }
            navigation.navigate('CreditScoreRegistration');
            return;
        }
        if (isReviewInStack) {
            navigation.popTo('CreditScoreReviewAddress', undefined, {
                merge: true,
            });
            return;
        }
        navigation.popTo('CreditScoreOnboarding', undefined, { merge: true });
        navigation.navigate('CreditScoreReviewAddress');
    }, [fetchSave, hideLoading, register, showLoading]);
    return { navigateToNextStep, continueAddress, navigateToAddress };
};
const styleSet = createStyleSheets((colors) => ({
    addressModalContent: {
        width: '100%',
        marginTop: rem(16),
        backgroundColor: colors.background.dark,
    },
    paragraph: {
        marginTop: rem(12),
    },
    status: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
}));
export default useCreditScoreNavigation;
